// src/pages/NotFound.js

import React from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Paper,
    Grid,
    useTheme
} from '@mui/material';
import {
    Home as HomeIcon,
    ArrowBack as ArrowBackIcon,
    Dashboard as DashboardIcon,
    SearchOff as SearchOffIcon,
    Construction as ConstructionIcon,
    SentimentDissatisfied as SadIcon,
    ErrorOutlineOutlined
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const NotFound = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const containerVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const iconVariants = {
        hidden: { scale: 0, rotate: -180 },
        visible: {
            scale: 1,
            rotate: 0,
            transition: {
                type: "spring",
                stiffness: 260,
                damping: 20,
                delay: 0.2
            }
        }
    };

    const buttonVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                duration: 0.4,
                delay: 0.6
            }
        },
        hover: {
            scale: 1.05,
            boxShadow: "0px 5px 15px rgba(0,0,0,0.2)",
            transition: {
                duration: 0.2
            }
        },
        tap: {
            scale: 0.95
        }
    };

    const quickLinks = [
        { 
            label: 'Dashboard', 
            icon: <DashboardIcon />, 
            path: '/dashboard',
            color: theme.palette.primary.main
        },
        { 
            label: 'Properties', 
            icon: <HomeIcon />, 
            path: '/properties',
            color: theme.palette.success.main
        },
        { 
            label: 'Tenants', 
            icon: <HomeIcon />, 
            path: '/tenants',
            color: theme.palette.info.main
        },
        { 
            label: 'Payments', 
            icon: <HomeIcon />, 
            path: '/payment-history',
            color: theme.palette.warning.main
        }
    ];

    return (
        <Container maxWidth="md" sx={{ 
            minHeight: '80vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
        }}>
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                style={{ width: '100%' }}
            >
                <Paper 
                    elevation={3} 
                    sx={{ 
                        p: { xs: 4, md: 6 }, 
                        borderRadius: 4,
                        textAlign: 'center',
                        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.light}15 100%)`,
                        border: `1px solid ${theme.palette.divider}`,
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    {/* Background decoration */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -50,
                            right: -50,
                            width: 200,
                            height: 200,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${theme.palette.primary.main}10, transparent 70%)`,
                            zIndex: 0
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -30,
                            left: -30,
                            width: 150,
                            height: 150,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${theme.palette.secondary.main}10, transparent 70%)`,
                            zIndex: 0
                        }}
                    />

                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        {/* 404 Icon */}
                        <motion.div variants={iconVariants}>
                            <Box sx={{ mb: 3, position: 'relative', display: 'inline-block' }}>
                                <SearchOffIcon 
                                    sx={{ 
                                        fontSize: 120, 
                                        color: theme.palette.primary.light,
                                        opacity: 0.3
                                    }} 
                                />
                                <ErrorOutlineOutlined
                                    sx={{ 
                                        fontSize: 50, 
                                        color: theme.palette.warning.main,
                                        position: 'absolute',
                                        top: -10,
                                        right: -20,
                                        animation: 'pulse 2s infinite'
                                    }} 
                                />
                            </Box>
                        </motion.div>

                        {/* 404 Text */}
                        <Typography 
                            variant="h1" 
                            sx={{ 
                                fontWeight: 800, 
                                fontSize: { xs: '5rem', md: '8rem' },
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                lineHeight: 1,
                                mb: 2,
                                letterSpacing: '-0.05em'
                            }}
                        >
                            404
                        </Typography>

                        {/* Main Message */}
                        <Typography 
                            variant="h4" 
                            sx={{ 
                                fontWeight: 700, 
                                color: theme.palette.text.primary,
                                mb: 1
                            }}
                        >
                            Page Not Found
                        </Typography>

                        {/* Sub Message */}
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: theme.palette.text.secondary,
                                mb: 2,
                                maxWidth: 500,
                                mx: 'auto'
                            }}
                        >
                            Oops! The page you're looking for doesn't exist or has been moved.
                            It might have been removed, renamed, or is temporarily unavailable.
                        </Typography>

                        {/* System Info */}
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: theme.palette.text.disabled,
                                mb: 4,
                                fontStyle: 'italic'
                            }}
                        >
                            Rental Collection Management System (RCMS)
                        </Typography>

                        {/* Action Buttons */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                            mb: 5
                        }}>
                            <motion.div 
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => navigate(-1)}
                                    sx={{
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                        boxShadow: `0 4px 15px ${theme.palette.primary.main}40`
                                    }}
                                >
                                    Go Back
                                </Button>
                            </motion.div>

                            <motion.div 
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                <Button
                                    variant="outlined"
                                    size="large"
                                    startIcon={<DashboardIcon />}
                                    onClick={() => navigate('/dashboard')}
                                    sx={{
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        borderWidth: 2,
                                        '&:hover': {
                                            borderWidth: 2
                                        }
                                    }}
                                >
                                    Dashboard
                                </Button>
                            </motion.div>
                        </Box>

                        {/* Quick Links */}
                        <Box sx={{ mt: 2 }}>
                            <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                    color: theme.palette.text.secondary,
                                    mb: 2,
                                    textTransform: 'uppercase',
                                    letterSpacing: 1
                                }}
                            >
                                Quick Links
                            </Typography>
                            <Grid container spacing={2} justifyContent="center">
                                {quickLinks.map((link, index) => (
                                    <Grid item key={index}>
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ 
                                                opacity: 1, 
                                                y: 0,
                                                transition: { delay: 0.8 + index * 0.1 }
                                            }}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            <Button
                                                variant="text"
                                                startIcon={link.icon}
                                                onClick={() => navigate(link.path)}
                                                sx={{
                                                    color: link.color,
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    '&:hover': {
                                                        backgroundColor: `${link.color}10`
                                                    }
                                                }}
                                            >
                                                {link.label}
                                            </Button>
                                        </motion.div>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        {/* Footer */}
                        <Box sx={{ 
                            mt: 4, 
                            pt: 3, 
                            borderTop: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1
                        }}>
                            <ConstructionIcon sx={{ fontSize: 16, color: theme.palette.text.disabled }} />
                            <Typography variant="caption" color="text.disabled">
                                If you believe this is an error, please contact your system administrator
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </motion.div>

            {/* CSS Animation for pulse effect */}
            <style>{`
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.1); opacity: 0.7; }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}</style>
        </Container>
    );
};

export default NotFound;